import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import Loader from '../../../shared/components/ui/Loader';
import {
  EmptyRow,
  SegmentedControl,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tr
} from '../../../shared/components/ui/primitives';
import { buildReturnToFromLocation, navigateWithReturn } from '../../../shared/navigation';
import { getSiteWallets } from '../../services/api/implementation/impl';
import { CreateWalletModal, WalletActionButtons } from './WalletActions';
import { HolderName, SmallButton, WalletStatusPill, money, shortDate } from './siteWalletUi';

/**
 * Every wallet with its numbers. The builder side and Accounts can add money;
 * only the builder side gets Create, Freeze and Settle.
 */
const WalletsPanel = ({ role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('open');
  const [creating, setCreating] = useState(false);

  const wallets = useQuery({
    queryKey: ['siteWallets', view],
    queryFn: () => getSiteWallets(view === 'closed' ? { status: 'CLOSED' } : {})
  });
  const rows = wallets.data ?? [];
  const actions = role.isStaff && view === 'open';

  const openWallet = (id) =>
    navigateWithReturn(navigate, `/builder/accounts/site-wallets/${id}`, {
      returnTo: buildReturnToFromLocation(location)
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          ariaLabel="Wallets"
          size="sm"
          value={view}
          onChange={setView}
          options={[
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' }
          ]}
        />
        {role.builderSide && (
          <SmallButton size="md" tone="primary" onClick={() => setCreating(true)}>
            <Plus size={14} /> Create wallet
          </SmallButton>
        )}
      </div>

      {wallets.isLoading ? (
        <Loader />
      ) : (
        <TableCard>
          <Table>
            <THead>
              <tr>
                <Th>Site engineer</Th>
                <Th align="right">Projects</Th>
                <Th align="right">Balance</Th>
                <Th align="right">On hold</Th>
                <Th align="right">Owed to them</Th>
                <Th>Last top-up</Th>
                <Th>Last expense</Th>
                <Th>Status</Th>
                {actions && <Th />}
              </tr>
            </THead>
            <TBody>
              {!rows.length && (
                <EmptyRow colSpan={actions ? 9 : 8}>
                  {view === 'closed' ? 'No closed wallets.' : 'No wallets yet.'}
                </EmptyRow>
              )}
              {rows.map((w) => (
                <Tr key={w.id} onClick={() => openWallet(w.id)}>
                  <Td>
                    <HolderName user={w.user} />
                  </Td>
                  <Td align="right" numeric>
                    {w.project_count}
                  </Td>
                  <Td
                    align="right"
                    numeric
                    className={`font-semibold ${Number(w.balance) < 0 ? 'text-danger-fg' : ''}`}>
                    {money(w.balance)}
                    {view === 'closed' && Number(w.settle_payout) > 0 && (
                      <div className="text-sm font-normal text-gray-500">
                        Payout {money(w.settle_payout)}
                        {w.settle_payout_paid_at ? ' paid' : ' due'}
                      </div>
                    )}
                  </Td>
                  <Td align="right" numeric className="text-gray-600">
                    {money(w.on_hold)}
                  </Td>
                  <Td
                    align="right"
                    numeric
                    className={Number(w.owed_to_holder) > 0 ? 'text-warning-fg' : 'text-gray-600'}>
                    {money(w.owed_to_holder)}
                  </Td>
                  <Td className="whitespace-nowrap text-gray-600">{shortDate(w.last_top_up_at)}</Td>
                  <Td className="whitespace-nowrap text-gray-600">{shortDate(w.last_expense_at)}</Td>
                  <Td>
                    <WalletStatusPill status={w.status} />
                  </Td>
                  {actions && (
                    <Td>
                      <WalletActionButtons size="md" wallet={w} role={role} />
                    </Td>
                  )}
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableCard>
      )}

      {creating && <CreateWalletModal onClose={() => setCreating(false)} />}
    </div>
  );
};

export default WalletsPanel;
